// @todo: Migrate to 'SkipToContent' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {
  Content as DropdownContent,
  Portal as DropdownPortal,
  Root as DropdownRoot,
  Separator as DropdownSeparator,
} from '@wandb/weave/components/DropdownMenu';
// @todo: Migrate to 'Icon' from @wandb/components. For more details see:
// https://wandb.github.io/components/?path=/docs/migration-guide--docs
// eslint-disable-next-line wandb/no-deprecated-imports
import {Icon} from '@wandb/weave/components/Icon';
import React, {useState} from 'react';
import {useHistory} from 'react-router-dom';

import {useRunDetailsTo} from '../../../components/RunDetails/hooks';
import {useIsInLoadedReportContext} from '../../../pages/ReportPage/LoadedReportContext';
import {RenameRunDialog} from '../../../pages/Run/RenameRunDialog';
import {
  type RunMenuAction,
  trackRunMenu,
} from '../../../services/analytics/runMenuEvents';
import {type Ref as CustomRunNamesRef} from '../../../state/views/customRunNames/types';
import {type WBTableRowWithRunSetInfo} from '../../../util/tablecols';
import {useRunSelectorFeatureEnabled} from '../RunSelectorFeaturesContext';
import {useRunSetGroupSelectionGrouping} from '../RunSelectorRunSetContext';
import {RowHeaderMenuOption} from './RowHeaderMenuOption';
import {RowHeaderMenuTrigger} from './RowHeaderMenuTrigger';
import {RunCopyIdentifiersSubmenu} from './RunCopyIdentifiersSubmenu';
import {RunPinningAndBaselineMenuOptions} from './RunPinningAndBaselineMenuOptions';

function useCanPinRuns() {
  const isEnabled = useRunSelectorFeatureEnabled();
  const isPinningRunsEnabled = isEnabled('pinning');
  const grouping = useRunSetGroupSelectionGrouping();
  return isPinningRunsEnabled && grouping.length === 0;
}

type RunRowHeaderMenuProps = {
  row: WBTableRowWithRunSetInfo;
  customRunNamesRef: CustomRunNamesRef | undefined;
  onCopyRunName: () => void;
  onCopyRunPath: () => void;
  onDeleteRun: () => void;
  onMoveRun: () => void;
  onStopRun: () => void;
  canStopRun?: boolean;
  visible: boolean;
};

export function RunRowHeaderMenu(props: RunRowHeaderMenuProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const isReport = useIsInLoadedReportContext();
  const canPinRuns = useCanPinRuns();
  const history = useHistory();
  const trackRunMenuWithLocation = (action: RunMenuAction) => {
    trackRunMenu({action, location: isReport ? 'report' : 'workspace'});
  };
  const runUrl = useRunDetailsTo(props.row.name);

  return (
    <>
      <DropdownRoot open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
        <RowHeaderMenuTrigger
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          visible={menuOpen || props.visible}
        />
        <DropdownPortal>
          <DropdownContent
            align="start"
            sideOffset={8}
            alignOffset={-8}
            style={{zIndex: 1}}>
            <RowHeaderMenuOption
              icon={<Icon name="code-alt" />}
              label="View run logs"
              onSelect={() => {
                history.push(runUrl('logs'));
                trackRunMenuWithLocation('View run details');
              }}
            />

            {canPinRuns ? (
              <RunPinningAndBaselineMenuOptions row={props.row} />
            ) : null}
            <DropdownSeparator />
            <RowHeaderMenuOption
              icon={<Icon name="pencil-edit" />}
              label="Edit run name"
              onSelect={() => {
                setIsRenameDialogOpen(true);
                trackRunMenuWithLocation('Edit run name');
              }}
            />
            <RunCopyIdentifiersSubmenu
              onCopyRunName={() => {
                props.onCopyRunName();
                trackRunMenuWithLocation('Copy run name');
              }}
              onCopyRunPath={() => {
                props.onCopyRunPath();
                trackRunMenuWithLocation('Copy run path');
              }}
            />
            <DropdownSeparator />

            <RowHeaderMenuOption
              icon={<Icon name="forward-next" />}
              label="Move to another group"
              onSelect={() => {
                props.onMoveRun();
                trackRunMenuWithLocation('Move run groups');
              }}
            />
            <RowHeaderMenuOption
              icon={<Icon name="delete" />}
              label="Delete run"
              onSelect={() => {
                props.onDeleteRun();
                trackRunMenuWithLocation('Delete run');
              }}
            />
            {props.canStopRun ? (
              <RowHeaderMenuOption
                icon={<Icon name="stop" />}
                label="Stop run"
                onSelect={() => {
                  props.onStopRun();
                  trackRunMenuWithLocation('Stop run');
                }}
              />
            ) : null}
          </DropdownContent>
        </DropdownPortal>
      </DropdownRoot>
      {isRenameDialogOpen && (
        <RenameRunDialog
          runId={props.row.id}
          runName={props.row.name}
          runDisplayName={props.row.displayName}
          customRunNamesRef={props.customRunNamesRef}
          closeDialog={() => setIsRenameDialogOpen(false)}
        />
      )}
    </>
  );
}
